import React, { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PhotoInput from "../../../components/ui/PhotoInput";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { createMyPet } from "../data/pets.service";
import Spinner from "../../../components/ui/Spinner";
import { useToast } from "../../../components/ui/ToastProvider";

export default function CreatePetDialog({
    open = false,
    onClose = () => { },
    onSuccess,
}) {
    const { t } = useTranslation();
    const initialFocusRef = useRef(null);

    const [photo, setPhoto] = useState({ file: null, previewUrl: "" });
    const [name, setName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { show } = useToast();
    const navigate = useNavigate();

    const nameOk = name.trim().length > 0;

    const hardReset = () => {
        setPhoto({ file: null, previewUrl: "" });
        setName("");
        setSubmitting(false);
    };

    const handleClose = () => {
        if (submitting) return;
        hardReset();
        onClose();
    };

    const handleCreate = async () => {
        if (!nameOk || submitting) return;
        try {
            setSubmitting(true);
            const { data, status } = await createMyPet({
                name: name.trim(),
                photoFile: photo.file || null,
                photoDataUrl: photo.previewUrl || null,
            });

            if (status === 200 || status === 201) {
                const pet = data?.pet || data;
                onSuccess?.(pet);
                hardReset();
                onClose(); // Close the dialog
                if (pet?.id) {
                    navigate(`/pets/${pet.id}`);
                }
            } else {
                show({ variant: "error", title: t('common.error'), description: t('pets.error_creating_pet') });
            }
        } catch (e) {
            show({ variant: "error", title: t('common.error'), description: e?.response?.data?.errors?.[0] || t('pets.error_creating_pet') });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose} initialFocus={initialFocusRef}>
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/50 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 flex items-end sm:items-center sm:justify-center">
                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95">
                        <Dialog.Panel className="relative w-full sm:w-[480px] bg-white rounded-t-[2.5rem] sm:rounded-[2rem] flex flex-col mt-4 sm:mt-0 overflow-hidden shadow-2xl h-[78vh] sm:h-auto sm:max-h-[85vh]">
                            {/* Joyful Hero Header - Proportional Height */}
                            <div className="relative h-60 sm:h-72 bg-white overflow-visible flex-shrink-0">
                                <div className="absolute inset-0 overflow-hidden rounded-t-[2.5rem] sm:rounded-t-[2rem]">
                                    <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-yellow-50/50 to-transparent" />
                                    <img 
                                        src="/images/happy_pets.png" 
                                        alt={t('pets.happy_pets_alt', { defaultValue: "Happy pets" })} 
                                        className="absolute inset-0 w-full h-full object-cover object-center scale-110"
                                    />
                                    {/* Waves */}
                                    <div className="absolute -bottom-1 inset-x-0 h-40 pointer-events-none z-20">
                                        <svg viewBox="0 0 500 150" preserveAspectRatio="none" className="h-[105%] w-full">
                                            <path 
                                                d="M0,100 C150,150 350,50 500,120 L500,150 L0,150 Z" 
                                                className="fill-white"
                                            />
                                            <rect x="0" y="145" width="500" height="10" className="fill-white" />
                                        </svg>
                                    </div>
                                    <div className="absolute bottom-0 inset-x-0 h-4 bg-white z-10" />
                                </div>
                                
                                {/* Photo input wrapper */}
                                <div className="absolute bottom-4 inset-x-0 flex justify-center translate-y-1/2 z-20">
                                    <PhotoInput
                                        value={photo.previewUrl}
                                        onChange={setPhoto}
                                        label=""
                                        helpText=""
                                        className="!rounded-full w-36 h-36 sm:w-36 sm:h-36 border-4 border-white"
                                    />
                                </div>
                                
                                {/* Joyful Floating Accents */}
                                <div className="absolute top-8 left-8 text-2xl animate-bounce z-10" style={{ animationDelay: '0.1s' }}>🐶</div>
                                <div className="absolute top-12 right-12 text-2xl animate-bounce z-10" style={{ animationDelay: '0.4s' }}>✨</div>
                            </div>

                            {/* Content area - Compact enough to avoid scrolling */}
                            <div className="flex-1 bg-white pt-20 pb-4 overflow-hidden px-5 sm:px-8">
                                <div className="space-y-6 relative z-10">
                                    <div className="px-2">
                                        <Input
                                            id="pet-name"
                                            type="text"
                                            placeholder={t('pets.add_pet_name_placeholder', { defaultValue: "What's their name?" })}
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            className="w-full text-center text-3xl sm:text-4xl py-4 border-none shadow-none focus:ring-0 placeholder:text-gray-300 font-extrabold tracking-tight"
                                            autoComplete="off"
                                        />
                                        
                                        {/* AI Tip - Aligned with input width */}
                                        <p className="mt-4 text-center text-xs sm:text-sm text-gray-400 font-medium w-full mx-auto leading-relaxed">
                                            {t('pets.ai_tip_simple', { defaultValue: "Protege y cuida a tu mascota con la ayuda de nuestra inteligencia artificial ✨" })}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Bottom buttons - Now in a single row on all screens */}
                            <div className="border-t border-gray-100 bg-white px-5 sm:px-8 py-4 flex-shrink-0">
                                <div className="flex flex-row-reverse gap-3">
                                    <Button
                                        variant="primary"
                                        className="flex-1 font-bold h-12"
                                        disabled={!nameOk || submitting}
                                        onClick={handleCreate}
                                    >
                                        {submitting ? (
                                            <span className="inline-flex items-center gap-2">
                                                <Spinner className="w-4 h-4" />
                                            </span>
                                        ) : (
                                            t('pets.create', { defaultValue: "Register" })
                                        )}
                                    </Button>
                                    <Button
                                        variant="neutral"
                                        className="flex-1 font-bold h-12"
                                        onClick={handleClose}
                                        disabled={submitting}
                                    >
                                        {t('common.cancel')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
